import { useMemo } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { getColorFromRgb } from '../common';
import store from '../store';
import { getActiveLayerIds, isLine, isPoint, isPolygon, LINE_WIDTH, POINT_RADIUS } from './common';

export default function FeatureLayers() {
  const { hoveredFeature, selectedFeature, projectIntelligence, activeSection, config } = useSnapshot(
    store,
  ) as typeof store;

  const getPolygonLayer = (featureId: string, f: GeoJSON.Feature) => {
    const item = projectIntelligence[featureId];
    return (
      <MapGL.Source key={`polygon-feature-${featureId}`} id={featureId} type="geojson" data={f}>
        <MapGL.Layer
          source={featureId}
          type="fill"
          paint={{
            'fill-color': getColorFromRgb(item.rgb) || 'red',
            'fill-opacity': hoveredFeature || selectedFeature ? 0 : 0.25,
          }}
        />
        <MapGL.Layer
          source={featureId}
          type="line"
          paint={{
            'line-width': LINE_WIDTH,
            'line-color': getColorFromRgb(item.rgb) || 'red',
          }}
        />
      </MapGL.Source>
    );
  };

  const getLineLayer = (featureId: string, f: GeoJSON.Feature) => {
    const item = projectIntelligence[featureId];
    return (
      <MapGL.Source key={`line-feature-${featureId}`} id={featureId} type="geojson" data={f}>
        <MapGL.Layer
          source={featureId}
          type="line"
          paint={{
            'line-width': LINE_WIDTH * 2,
            'line-color': getColorFromRgb(item.rgb) || 'red',
          }}
        />
      </MapGL.Source>
    );
  };

  const getPointLayer = (featureId: string, f: GeoJSON.Feature, layerId: string) => {
    const item = projectIntelligence[featureId];

    return (
      <MapGL.Source key={`point-feature-${featureId}`} id={featureId} type="geojson" data={f}>
        <MapGL.Layer
          source={featureId}
          type="circle"
          id={featureId}
          paint={{
            'circle-stroke-color': getColorFromRgb(item.rgb) || 'red',
            'circle-stroke-width': LINE_WIDTH * 3,
            'circle-radius': POINT_RADIUS * 1.5,
            'circle-color': 'transparent',
          }}
        />
      </MapGL.Source>
    );
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const featuresOnMap = useMemo(() => {
    const layerIds = getActiveLayerIds(activeSection);
    const els: JSX.Element[] = [];

    layerIds?.forEach((id) => {
      if (id !== selectedFeature?.id?.toString() && id !== hoveredFeature?.id?.toString()) {
        const feats = config[id];

        if (feats) {
          Object.keys(feats)?.forEach((featureId, i) => {
            const f = feats[featureId];
            let feature: JSX.Element | null = null;

            if (isPolygon(f)) {
              feature = getPolygonLayer(featureId, f);
            } else if (isLine(f)) {
              feature = getLineLayer(featureId, f);
            } else if (isPoint(f)) {
              feature = getPointLayer(featureId, f, id);
            }

            if (feature) {
              els.push(feature);
            }
          });
        }
      }
    });

    return els;
  }, [activeSection, hoveredFeature, selectedFeature, config]);

  return <>{featuresOnMap}</>;
}
